import React, { useState, useRef, useEffect } from 'react'
import { t } from '@lingui/core/macro'
import styles from './FormSelect.module.css'

export interface FormSelectItem {
    value: string
    name: string
}

interface FormSelectProps {
    label: string
    value: string
    items: FormSelectItem[]
    placeholder: string
    searchPlaceholder: string
    onChange: (value: string) => void
    errorMessage?: string
}

const FormSelect = (
    { label, value, items, placeholder, searchPlaceholder, onChange, errorMessage }: FormSelectProps
) => {
    const [isOpen, setIsOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)

    const selectedItem = items.find(item => item.name === value || item.value === value)

    const filteredItems = items.filter(item => {
        const query = searchQuery.toLowerCase()

        return (
            item.value.toLowerCase().includes(query) ||
            item.name.toLowerCase().includes(query)
        )
    })

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
                setSearchQuery('')
            }
        }

        document.addEventListener('mousedown', handleClickOutside)

        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isOpen])

    const handleSelect = (item: FormSelectItem) => {
        onChange(item.name)

        setIsOpen(false)
        setSearchQuery('')
    }

    const handleClear = () => {
        onChange('')
    }

    return (
        <div className={styles.inputGroup}>
            <label className={styles.label}>{label}</label>

            <div className={styles.selectWrapper} ref={dropdownRef}>
                <div className={styles.inputWrapper}>
                    <button
                        type="button"
                        className={`${styles.selectButton} ${errorMessage ? styles.inputError : ''}`}
                        onClick={() => setIsOpen(!isOpen)}
                    >
                        <span className={styles.selectValue}>{selectedItem ? selectedItem.value : placeholder}</span>
                    </button>

                    <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>▼</span>
                    {selectedItem && (
                        <button type="button" className={styles.clearButton} onClick={handleClear}>×</button>
                    )}
                </div>

                {isOpen && (
                    <div className={styles.dropdown}>
                        <div className={styles.searchWrapper}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                className={styles.searchInput}
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <ul className={styles.optionsList}>
                            {filteredItems.length > 0 ? (
                                filteredItems.map(items => (
                                    <li
                                        key={items.name}
                                        className={`${styles.option} ${
                                            selectedItem?.name === items.name ? styles.optionSelected : ''
                                        }`}
                                        onClick={() => handleSelect(items)}
                                    >
                                        <span className={styles.itemValue}>{items.value}</span>
                                        <span className={styles.itemName}>{items.name}</span>
                                    </li>
                                ))
                            ) : (
                                <li className={styles.noResults}>{t`Ничего не найдено`}</li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
            {errorMessage && <span className={styles.error}>{errorMessage}</span>}
        </div>
    )
}

export default FormSelect
